import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Link2Off } from "lucide-react";
import {
  ErrorCode,
  PLATFORM_ENDPOINT_LOCAL_NAME_MAX_LENGTH,
  type FacebookConnectedPage,
  type InstagramConnectedAccount,
} from "@multi-chat/shared";
import { updatePlatformEndpointLocalName } from "../../api/platform-endpoint-api.js";
import { isApiError } from "../../api/client.js";
import {
  ChannelDetailFrame,
  ChannelDetailSkeleton,
  ConnectedChannelActions,
  ConnectedChannelCard,
  ConnectedChannelDetailList,
  ConnectedChannelDetailRow,
  ConnectedChannelHeader,
  ConnectedChannelPage,
} from "../../components/channel/ConnectedChannelLayout.js";
import { AppNotice } from "../../components/common/AppNotice.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import { ConfirmDisconnectDialog } from "../../components/common/ConfirmDisconnectDialog.js";
import { useFacebookConnection } from "../whatsapp/use-facebook-connection.js";
import { useFacebookPopup } from "../whatsapp/use-facebook-popup.js";
import { FacebookPageSelection } from "../whatsapp/FacebookPage/FacebookPageSelection.js";
import { useNotifications } from "../notifications/notification-context.js";
import { useInstagramConnection } from "../instagram/use-instagram-connection.js";
import { useInstagramPopup } from "../instagram/use-instagram-popup.js";
import {
  formatInstagramUsername,
  instagramAccountTypeLabelKey,
} from "../instagram/instagram-connection-presentation.js";
import { MixedLocaleValue } from "../whatsapp/TechnicalValue.js";
import { ChannelConnectNotice } from "./ChannelConnectNotice.js";
import { canSaveChannelLocalName, channelCardTitle, trimmedChannelLocalName } from "./channel-local-name.js";
import { facebookPageUrl, instagramProfileUrl } from "./channel-list-menu.js";
import { useOrganizationPlatformFlags } from "../organizations/use-organization-platform-flags.js";
import { useChannelDetailHeader, useChannelDirectory } from "./ChannelsWorkspaceLayout.js";

const localNameFieldClass =
  "h-10 w-full min-w-0 rounded-lg border border-line-strong bg-surface px-3 text-start text-[0.8125rem] text-foreground transition-[border-color,box-shadow] duration-150 ease-in-out focus:border-accent focus:shadow-[0_0_0_3px_var(--color-ring)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-70";

const disconnectButtonClass =
  "h-9 gap-2 rounded-full border-destructive/30 bg-error-muted px-4 text-[0.8125rem] text-destructive shadow-none not-disabled:hover:border-destructive/50 not-disabled:hover:bg-error-muted";

type ConnectedPresentation = {
  title: string;
  saving: boolean;
};

export function FacebookChannelPage(): React.JSX.Element {
  const { t } = useTranslation();
  const directory = useChannelDirectory();
  const platformFlags = useOrganizationPlatformFlags(directory.organizationId);
  const facebookDisabled = platformFlags != null && !platformFlags.facebookEnabled;
  const connectionApi = useFacebookConnection(directory.organizationId, t("facebook.unreachable"));
  const popup = useFacebookPopup(() => {
    connectionApi.refresh();
    directory.reload();
  });
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const connection = connectionApi.view.kind === "ready" ? connectionApi.view.connection : null;
  const pending = connection?.state === "PENDING_PAGE_SELECTION";
  const showDisabledConnect =
    facebookDisabled &&
    (connection == null ||
      connection.state === "NOT_CONNECTED" ||
      connection.state === "DISCONNECTED" ||
      connection.state === "ERROR");
  const canConnect =
    !facebookDisabled &&
    connection?.permissions.canConnect === true &&
    (connection.state === "NOT_CONNECTED" ||
      connection.state === "DISCONNECTED" ||
      connection.state === "ERROR");

  const [presentation, setPresentation] = useState<ConnectedPresentation | null>(null);
  const onPresentationChange = useCallback((value: ConnectedPresentation | null) => {
    setPresentation(value);
  }, []);
  const connectedPage = connection?.state === "CONNECTED" ? connection.page : null;
  const fallbackTitle = channelCardTitle(
    connection?.page?.localName,
    connection?.page?.pageName?.trim() || t("channels.platforms.facebook"),
  );
  useChannelDetailHeader(
    connectionApi.view.kind === "loading"
      ? null
      : {
          title: presentation?.title ?? fallbackTitle,
          disconnect:
            connectedPage && connection?.permissions.canDisconnect
              ? {
                  label: t("facebook.connected.disconnect"),
                  busy: connectionApi.busy || (presentation?.saving ?? false),
                  testId: "channels-facebook-disconnect",
                  onClick: () => setConfirm(true),
                }
              : null,
        },
  );

  useEffect(() => {
    setSelectedPageId(null);
    setConfirm(false);
    popup.reset();
  }, [directory.organizationId, popup.reset]);

  const disconnectDialog = (
    <ConfirmDisconnectDialog
      open={confirm}
      onOpenChange={setConfirm}
      platformType="FACEBOOK"
      title={t("facebook.disconnectConfirm.title")}
      description={t("facebook.disconnectConfirm.description")}
      cancelLabel={t("facebook.disconnectConfirm.cancel")}
      confirmLabel={t("facebook.disconnectConfirm.confirm")}
      pendingLabel={t("facebook.disconnectConfirm.disconnecting")}
      busy={connectionApi.busy}
      onConfirm={() => {
        void connectionApi.disconnect().then(() => {
          setConfirm(false);
          directory.reload();
        });
      }}
      testId="channels-facebook-disconnect-dialog"
    />
  );

  if (connectionApi.view.kind === "loading") {
    return (
      <>
        <ChannelDetailSkeleton label={t("channels.home.loading")} testId="channel-detail-loading" />
        {disconnectDialog}
      </>
    );
  }

  if (connection?.state === "CONNECTED" && connection.page) {
    return (
      <>
        <FacebookConnectedDetails
          key={connection.page.platformEndpointId}
          organizationId={directory.organizationId}
          page={connection.page}
          canEdit={connection.permissions.canDisconnect}
          actionError={connectionApi.actionError}
          onSaved={() => directory.reload()}
          onPresentationChange={onPresentationChange}
        />
        {disconnectDialog}
      </>
    );
  }

  return (
    <>
      <ChannelDetailFrame
        testId="channels-facebook-page"
        platformType="FACEBOOK"
        title={connection?.page?.pageName ?? t("channels.platforms.facebook")}
        state={connection?.state ?? "NOT_CONNECTED"}
        error={connectionApi.view.kind === "error" || connectionApi.view.kind === "unavailable" ? connectionApi.view.message : connectionApi.actionError}
      >
        {connection?.page ? (
          <p className="m-0 text-[0.8125rem] text-muted">
            {t("facebook.connected.pageId")}{" "}
            <LtrValue className="font-mono">{connection.page.pageId}</LtrValue>
          </p>
        ) : showDisabledConnect ? (
          <AppNotice
            severity="error"
            description={t("channels.platformDisabled")}
            testId="channels-facebook-disabled"
          />
        ) : (
          <p className="m-0 max-w-md text-[0.8125rem] text-muted">{t("channels.connect.facebook.body")}</p>
        )}
        {pending && connection ? (
          <div className="w-full text-start">
            <FacebookPageSelection
              candidates={connection.pageCandidates ?? []}
              selectedPageId={selectedPageId}
              canSelectPage={connection.permissions.canSelectPage}
              canCancelPending={connection.permissions.canCancelPending}
              busy={connectionApi.busy}
              onSelectPage={setSelectedPageId}
              onSubmitSelection={() => {
                if (!selectedPageId) return;
                void connectionApi.selectPage(selectedPageId).then(() => directory.reload());
              }}
              onCancelPending={() => {
                void connectionApi.cancelPending().then(() => directory.reload());
              }}
            />
          </div>
        ) : null}
        {popup.status.kind === "error" ? (
          <ChannelConnectNotice
            platform="facebook"
            code={popup.status.code}
            onDismiss={popup.reset}
          />
        ) : null}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {canConnect ? (
            <Button
              type="button"
              data-testid="channels-facebook-connect"
              disabled={popup.status.kind === "opening" || connectionApi.busy}
              onClick={() => {
                if (facebookDisabled) return;
                popup.reset();
                popup.start(directory.organizationId);
              }}
            >
              {t("channels.connect.facebook.action")}
            </Button>
          ) : null}
          {connection?.permissions.canDisconnect && connection.page ? (
            <Button
              type="button"
              variant="outline"
              className={disconnectButtonClass}
              data-testid="channels-facebook-disconnect"
              onClick={() => setConfirm(true)}
            >
              <Link2Off className="size-4" aria-hidden="true" />
              {t("facebook.connected.disconnect")}
            </Button>
          ) : null}
        </div>
      </ChannelDetailFrame>
      {disconnectDialog}
    </>
  );
}

export function InstagramChannelPage(): React.JSX.Element {
  const { t } = useTranslation();
  const directory = useChannelDirectory();
  const platformFlags = useOrganizationPlatformFlags(directory.organizationId);
  const instagramDisabled = platformFlags != null && !platformFlags.instagramEnabled;
  const connectionApi = useInstagramConnection(directory.organizationId, t("instagram.unreachable"));
  const popup = useInstagramPopup(() => {
    connectionApi.refresh();
    directory.reload();
  });
  const [confirm, setConfirm] = useState(false);
  const connection = connectionApi.view.kind === "ready" ? connectionApi.view.connection : null;
  const account = connection?.account ?? null;
  const title =
    account?.name?.trim() ||
    formatInstagramUsername(account?.username) ||
    t("channels.platforms.instagram");
  const showDisabledConnect =
    instagramDisabled &&
    (connection == null ||
      connection.state === "NOT_CONNECTED" ||
      connection.state === "DISCONNECTED" ||
      connection.state === "ERROR");
  const canConnect =
    !instagramDisabled &&
    connection?.permissions.canConnect === true &&
    (connection.state === "NOT_CONNECTED" ||
      connection.state === "DISCONNECTED" ||
      connection.state === "ERROR");

  const [presentation, setPresentation] = useState<ConnectedPresentation | null>(null);
  const onPresentationChange = useCallback((value: ConnectedPresentation | null) => {
    setPresentation(value);
  }, []);
  const connectedAccount = connection?.state === "CONNECTED" ? connection.account : null;
  const fallbackTitle = channelCardTitle(account?.localName, title);
  useChannelDetailHeader(
    connectionApi.view.kind === "loading"
      ? null
      : {
          title: presentation?.title ?? fallbackTitle,
          disconnect:
            connectedAccount && connection?.permissions.canDisconnect
              ? {
                  label: t("instagram.connected.disconnect"),
                  busy: connectionApi.busy || (presentation?.saving ?? false),
                  testId: "channels-instagram-disconnect",
                  onClick: () => setConfirm(true),
                }
              : null,
        },
  );

  useEffect(() => {
    setConfirm(false);
    popup.reset();
  }, [directory.organizationId, popup.reset]);

  const disconnectDialog = (
    <ConfirmDisconnectDialog
      open={confirm}
      onOpenChange={setConfirm}
      platformType="INSTAGRAM"
      title={t("instagram.disconnectConfirm.title")}
      description={t("instagram.disconnectConfirm.description")}
      cancelLabel={t("instagram.disconnectConfirm.cancel")}
      confirmLabel={t("instagram.disconnectConfirm.confirm")}
      pendingLabel={t("instagram.disconnectConfirm.disconnecting")}
      busy={connectionApi.busy}
      onConfirm={() => {
        void connectionApi.disconnect().then(() => {
          setConfirm(false);
          directory.reload();
        });
      }}
      testId="channels-instagram-disconnect-dialog"
    />
  );

  if (connectionApi.view.kind === "loading") {
    return (
      <>
        <ChannelDetailSkeleton label={t("channels.home.loading")} testId="channel-detail-loading" />
        {disconnectDialog}
      </>
    );
  }

  if (connection?.state === "CONNECTED" && connection.account) {
    return (
      <>
        <InstagramConnectedDetails
          key={connection.account.platformEndpointId}
          organizationId={directory.organizationId}
          account={connection.account}
          canEdit={connection.permissions.canDisconnect}
          actionError={connectionApi.actionError}
          onSaved={() => directory.reload()}
          onPresentationChange={onPresentationChange}
        />
        {disconnectDialog}
      </>
    );
  }

  return (
    <>
      <ChannelDetailFrame
        testId="channels-instagram-page"
        platformType="INSTAGRAM"
        title={title}
        state={connection?.state ?? "NOT_CONNECTED"}
        error={connectionApi.view.kind === "error" || connectionApi.view.kind === "unavailable" ? connectionApi.view.message : connectionApi.actionError}
      >
        {account ? (
          <p className="m-0 text-[0.8125rem] text-muted">
            {t("channels.details.accountId")}{" "}
            <LtrValue className="font-mono">{account.instagramUserId}</LtrValue>
          </p>
        ) : showDisabledConnect ? (
          <AppNotice
            severity="error"
            description={t("channels.platformDisabled")}
            testId="channels-instagram-disabled"
          />
        ) : (
          <p className="m-0 max-w-md text-[0.8125rem] text-muted">{t("channels.connect.instagram.body")}</p>
        )}
        {popup.status.kind === "error" ? (
          <ChannelConnectNotice
            platform="instagram"
            code={popup.status.code}
            onDismiss={popup.reset}
          />
        ) : null}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {canConnect ? (
            <Button
              type="button"
              data-testid="channels-instagram-connect"
              disabled={popup.status.kind === "opening" || connectionApi.busy}
              onClick={() => {
                if (instagramDisabled) return;
                popup.reset();
                popup.start(directory.organizationId);
              }}
            >
              {t("channels.connect.instagram.action")}
            </Button>
          ) : null}
          {connection?.permissions.canDisconnect && account ? (
            <Button
              type="button"
              variant="outline"
              className={disconnectButtonClass}
              data-testid="channels-instagram-disconnect"
              onClick={() => setConfirm(true)}
            >
              <Link2Off className="size-4" aria-hidden="true" />
              {t("instagram.connected.disconnect")}
            </Button>
          ) : null}
        </div>
      </ChannelDetailFrame>
      {disconnectDialog}
    </>
  );
}

function InstagramConnectedDetails({
  organizationId,
  account,
  canEdit,
  actionError,
  onSaved,
  onPresentationChange,
}: {
  organizationId: number;
  account: InstagramConnectedAccount;
  canEdit: boolean;
  actionError: string | null;
  onSaved: () => void;
  onPresentationChange: (value: ConnectedPresentation | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const initialName = account.localName?.trim() ?? "";
  const [savedName, setSavedName] = useState(initialName);
  const [draft, setDraft] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  const usernameLabel = formatInstagramUsername(account.username);
  const profileName = account.name?.trim() ?? "";
  const headerName =
    savedName ||
    profileName ||
    usernameLabel ||
    t("channels.platforms.instagram");
  const accountTypeKey = instagramAccountTypeLabelKey(account.accountType);
  const profileUrl = instagramProfileUrl(account.username);
  const trimmedDraft = trimmedChannelLocalName(draft);
  const showNameError = canEdit && nameTouched && trimmedDraft.length === 0;
  const canSave = canSaveChannelLocalName({
    canEdit,
    saving,
    savedName,
    draft,
  });

  useEffect(() => {
    onPresentationChange({ title: headerName, saving });
  }, [headerName, saving, onPresentationChange]);

  useEffect(() => {
    return () => onPresentationChange(null);
  }, [onPresentationChange]);

  async function handleSave(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setNameTouched(true);
    if (!canSave || savingRef.current) return;
    const nextName = trimmedChannelLocalName(draft);
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updatePlatformEndpointLocalName(
        organizationId,
        account.platformEndpointId,
        nextName,
      );
      const stored = updated.localName.trim();
      setSavedName(stored);
      setDraft(stored);
      pushNotification({ message: t("channels.details.saved") });
      onSaved();
    } catch (error) {
      setSaveError(
        t(
          isApiError(error) && error.code === ErrorCode.VALIDATION_FAILED
            ? "channels.details.nameRequired"
            : "channels.details.saveError",
        ),
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <ConnectedChannelPage testId="channels-instagram-page">
      <ConnectedChannelHeader
        platformType="INSTAGRAM"
        title={<MixedLocaleValue className="wrap-anywhere">{headerName}</MixedLocaleValue>}
        badge={<Badge variant="success">{t("channels.status.connected")}</Badge>}
        identity={
          usernameLabel ? (
            <LtrValue className="wrap-anywhere">{usernameLabel}</LtrValue>
          ) : null
        }
      />

      {actionError ? <AppNotice severity="error" description={actionError} /> : null}

      <form
        onSubmit={(event) => void handleSave(event)}
        noValidate
        data-testid="channels-instagram-details-form"
      >
        <ConnectedChannelCard
          title={t("channels.details.settingsTitle")}
          testId="channels-instagram-settings"
        >
          <div className="grid gap-1.5">
            <label
              className="text-[0.8125rem] font-semibold text-foreground"
              htmlFor="channels-instagram-local-name"
            >
              {t("channels.details.localName")}
            </label>
            <input
              id="channels-instagram-local-name"
              data-testid="channels-instagram-local-name"
              type="text"
              className={localNameFieldClass}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setSaveError(null);
              }}
              onBlur={() => setNameTouched(true)}
              maxLength={PLATFORM_ENDPOINT_LOCAL_NAME_MAX_LENGTH}
              disabled={!canEdit || saving}
              readOnly={!canEdit}
              aria-invalid={showNameError}
              aria-describedby={
                showNameError
                  ? "channels-instagram-local-name-hint channels-instagram-local-name-error"
                  : "channels-instagram-local-name-hint"
              }
            />
            <p id="channels-instagram-local-name-hint" className="m-0 text-[0.75rem] leading-relaxed text-muted">
              {t("channels.details.localNameHint")}
            </p>
            {showNameError ? (
              <p
                id="channels-instagram-local-name-error"
                className="m-0 text-[0.75rem] text-error"
                role="alert"
              >
                {t("channels.details.nameRequired")}
              </p>
            ) : null}
          </div>
          {saveError ? (
            <AppNotice
              severity="error"
              description={saveError}
              testId="channels-instagram-save-error"
            />
          ) : null}
          {canEdit ? (
            <ConnectedChannelActions>
              <Button
                type="submit"
                size="sm"
                disabled={!canSave}
                data-testid="channels-instagram-save"
              >
                {saving ? t("channels.details.saving") : t("channels.details.save")}
              </Button>
            </ConnectedChannelActions>
          ) : null}
        </ConnectedChannelCard>
      </form>

      <ConnectedChannelCard
        title={t("channels.details.instagramAccountTitle")}
        testId="channels-instagram-account"
      >
        <ConnectedChannelDetailList>
          <ConnectedChannelDetailRow label={t("channels.details.profileName")}>
            <span data-testid="channels-instagram-profile-name">
              {profileName ? (
                <MixedLocaleValue className="wrap-anywhere">{profileName}</MixedLocaleValue>
              ) : (
                <span className="text-muted">{t("channels.details.empty")}</span>
              )}
            </span>
          </ConnectedChannelDetailRow>
          <ConnectedChannelDetailRow label={t("instagram.connected.username")}>
            <span data-testid="channels-instagram-username">
              <LtrValue className="wrap-anywhere">
                {usernameLabel ?? t("channels.details.empty")}
              </LtrValue>
            </span>
          </ConnectedChannelDetailRow>
          <ConnectedChannelDetailRow label={t("instagram.connected.accountType")}>
            <span data-testid="channels-instagram-account-type">
              {accountTypeKey ? t(accountTypeKey) : t("channels.details.empty")}
            </span>
          </ConnectedChannelDetailRow>
          <ConnectedChannelDetailRow label={t("channels.details.instagramUserId")}>
            <span data-testid="channels-instagram-user-id">
              <LtrValue className="font-mono text-[0.75rem] text-muted wrap-anywhere">
                {account.instagramUserId}
              </LtrValue>
            </span>
          </ConnectedChannelDetailRow>
        </ConnectedChannelDetailList>
        {profileUrl ? (
          <ConnectedChannelActions>
            <Button asChild variant="outline" size="sm">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="channels-instagram-open-profile"
              >
                <ExternalLink className="size-3.5" aria-hidden="true" />
                {t("channels.details.openInstagramProfile")}
              </a>
            </Button>
          </ConnectedChannelActions>
        ) : null}
      </ConnectedChannelCard>
    </ConnectedChannelPage>
  );
}

function FacebookConnectedDetails({
  organizationId,
  page,
  canEdit,
  actionError,
  onSaved,
  onPresentationChange,
}: {
  organizationId: number;
  page: FacebookConnectedPage;
  canEdit: boolean;
  actionError: string | null;
  onSaved: () => void;
  onPresentationChange: (value: ConnectedPresentation | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const initialName = page.localName?.trim() ?? "";
  const [savedName, setSavedName] = useState(initialName);
  const [draft, setDraft] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  const pageName = page.pageName.trim();
  const headerName = savedName || pageName || t("channels.platforms.facebook");
  const pageUrl = facebookPageUrl(page.pageId);
  const trimmedDraft = trimmedChannelLocalName(draft);
  const showNameError = canEdit && nameTouched && trimmedDraft.length === 0;
  const canSave = canSaveChannelLocalName({
    canEdit,
    saving,
    savedName,
    draft,
  });

  useEffect(() => {
    onPresentationChange({ title: headerName, saving });
  }, [headerName, saving, onPresentationChange]);

  useEffect(() => {
    return () => onPresentationChange(null);
  }, [onPresentationChange]);

  async function handleSave(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setNameTouched(true);
    if (!canSave || savingRef.current) return;
    const nextName = trimmedChannelLocalName(draft);
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updatePlatformEndpointLocalName(
        organizationId,
        page.platformEndpointId,
        nextName,
      );
      const stored = updated.localName.trim();
      setSavedName(stored);
      setDraft(stored);
      pushNotification({ message: t("channels.details.saved") });
      onSaved();
    } catch (error) {
      setSaveError(
        t(
          isApiError(error) && error.code === ErrorCode.VALIDATION_FAILED
            ? "channels.details.nameRequired"
            : "channels.details.saveError",
        ),
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <ConnectedChannelPage testId="channels-facebook-page">
      <ConnectedChannelHeader
        platformType="FACEBOOK"
        title={<MixedLocaleValue className="wrap-anywhere">{headerName}</MixedLocaleValue>}
        badge={<Badge variant="success">{t("channels.status.connected")}</Badge>}
        identity={
          pageName ? (
            <MixedLocaleValue className="wrap-anywhere">{pageName}</MixedLocaleValue>
          ) : null
        }
      />

      {actionError ? <AppNotice severity="error" description={actionError} /> : null}

      <form
        onSubmit={(event) => void handleSave(event)}
        noValidate
        data-testid="channels-facebook-details-form"
      >
        <ConnectedChannelCard
          title={t("channels.details.settingsTitle")}
          testId="channels-facebook-settings"
        >
          <div className="grid gap-1.5">
            <label
              className="text-[0.8125rem] font-semibold text-foreground"
              htmlFor="channels-facebook-local-name"
            >
              {t("channels.details.localName")}
            </label>
            <input
              id="channels-facebook-local-name"
              data-testid="channels-facebook-local-name"
              type="text"
              className={localNameFieldClass}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setSaveError(null);
              }}
              onBlur={() => setNameTouched(true)}
              maxLength={PLATFORM_ENDPOINT_LOCAL_NAME_MAX_LENGTH}
              disabled={!canEdit || saving}
              readOnly={!canEdit}
              aria-invalid={showNameError}
              aria-describedby={
                showNameError
                  ? "channels-facebook-local-name-hint channels-facebook-local-name-error"
                  : "channels-facebook-local-name-hint"
              }
            />
            <p id="channels-facebook-local-name-hint" className="m-0 text-[0.75rem] leading-relaxed text-muted">
              {t("channels.details.messengerLocalNameHint")}
            </p>
            {showNameError ? (
              <p
                id="channels-facebook-local-name-error"
                className="m-0 text-[0.75rem] text-error"
                role="alert"
              >
                {t("channels.details.nameRequired")}
              </p>
            ) : null}
          </div>
          {saveError ? (
            <AppNotice
              severity="error"
              description={saveError}
              testId="channels-facebook-save-error"
            />
          ) : null}
          {canEdit ? (
            <ConnectedChannelActions>
              <Button
                type="submit"
                size="sm"
                disabled={!canSave}
                data-testid="channels-facebook-save"
              >
                {saving ? t("channels.details.saving") : t("channels.details.save")}
              </Button>
            </ConnectedChannelActions>
          ) : null}
        </ConnectedChannelCard>
      </form>

      <ConnectedChannelCard
        title={t("channels.details.facebookPageTitle")}
        testId="channels-facebook-account"
      >
        <ConnectedChannelDetailList>
          <ConnectedChannelDetailRow label={t("facebook.connected.pageName")}>
            <span data-testid="channels-facebook-page-name">
              {pageName ? (
                <MixedLocaleValue className="wrap-anywhere">{pageName}</MixedLocaleValue>
              ) : (
                <span className="text-muted">{t("channels.details.empty")}</span>
              )}
            </span>
          </ConnectedChannelDetailRow>
          <ConnectedChannelDetailRow label={t("facebook.connected.pageId")}>
            <span data-testid="channels-facebook-page-id">
              <LtrValue className="font-mono text-[0.75rem] text-muted wrap-anywhere">
                {page.pageId}
              </LtrValue>
            </span>
          </ConnectedChannelDetailRow>
        </ConnectedChannelDetailList>
        {pageUrl ? (
          <ConnectedChannelActions>
            <Button asChild variant="outline" size="sm">
              <a
                href={pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="channels-facebook-open-page"
              >
                <ExternalLink className="size-3.5" aria-hidden="true" />
                {t("channels.details.openFacebookPage")}
              </a>
            </Button>
          </ConnectedChannelActions>
        ) : null}
      </ConnectedChannelCard>
    </ConnectedChannelPage>
  );
}
